import { datadogRum } from '@datadog/browser-rum';
import { getUserState } from '@carvana/core-auth';
import { Experiments } from '@carvana/experiment/SharedTypes';
import Cookie from './Cookie';
import { Severity } from './datadog.types';
import type { DatadogInterface, DatadogSpan, LogMessage } from './datadog.types';

declare global {
  interface Window {
    Cypress: unknown;
  }
}

const moduleVersions: { [key: string]: string } = {};

const initializeDataDog = (experiments: Experiments) => {
  const isClient = typeof window === 'object';
  const { user } = getUserState();
  let existingCypress;
  if (isClient) {
    existingCypress = window.Cypress;
    window.Cypress = undefined;
  }

  datadogRum.init({
    applicationId: 'c0e19456-086b-48eb-b954-ea6abf661dcf',
    clientToken: 'pub1d8c8908f469710592832a49e98cf269',
    site: 'datadoghq.com',
    service: 'stc-ui',
    env: `${process.env.CARVANA_APP_ENVIRONMENT}`,
    version: `${process.env.APP_VERSION}`,
    // Specify a version number to identify the deployed version of your application in Datadog
    // version: '1.0.0',
    sampleRate: 100,
    trackInteractions: true,
    trackFrustrations: true,
    defaultPrivacyLevel: 'mask-user-input'
  });

  datadogRum.setUser({
    id: user.user_id,
    bcid: Cookie.getBcId(),
  });

  datadogRum.startSessionReplayRecording();
  const experimentWithAssignedBucket = Object.entries(experiments).reduce((acc, [key, value]) => ({
    ...acc,
    [key]: value.assignedBucket
  }), {});
  datadogRum.setGlobalContextProperty('vtExperiments', experimentWithAssignedBucket);
  datadogRum.addAction('Rum Initialized');

  if (isClient) {
    window.Cypress = existingCypress;
  }
};

const userAuthenticated = (userId: string) => {
  datadogRum.setUser({
    id: userId
  });
};
const clearAuthenticatedUser = () => {
  datadogRum.removeUser();
};
const startTrackingEvent = (component: string, name: string) => {
  const start = performance.now();
  return () => {
    const taken = performance.now() - start;
    datadogRum.addAction(`${component}:${name}`, {
      component,
      name,
      taken
    });
  };
};

const log = (message: LogMessage) => {
  if (message.severity >= Severity.Error) {
    datadogRum.addError(message.message, message);
  }
  datadogRum.addAction('LogMessage', message);
};

const logError = (err: unknown, message?: string) =>
  datadogRum.addError(err, {
    message
  });

const recordException = (exception: unknown, properties: object) =>
  datadogRum.addError(exception, properties);

const addAction = (eventName: string, context?: object) => {
  datadogRum.addAction(eventName, context);
};

export class DataDogSpanImpl implements DatadogSpan {
  private readonly measureStart: DOMHighResTimeStamp;

  private readonly start: Date;

  private readonly spanName: string;

  private readonly component: string;

  private measureEnd?: DOMHighResTimeStamp;

  private ended?: Date;

  constructor(component: string, name: string) {
    this.spanName = name;
    this.component = component;
    this.measureStart = performance.now();
    this.start = new Date();
  }

  private defaultParameters() {
    return {
      component: this.component,
      spanName: this.spanName,
      start: this.start
    };
  }

  addEvent(name: string, attributes?: unknown): this {
    const elapsedMs = performance.now() - this.measureStart;
    datadogRum.addAction(`${this.component}:${this.spanName}:${name}`, {
      ...this.defaultParameters(),
      name,
      elapsedMs,
      attributes: attributes ?? {}
    });
    return this;
  }

  addErrorEvent(name: string, attributes?: unknown, severity?: Severity): this {
    const elapsed = performance.now() - this.measureStart;
    datadogRum.addError(`${this.component}:${this.spanName}:${name}`, {
      severity: severity ?? Severity.Error,
      elapsed,
      attributes: attributes ?? {}
    });

    return this;
  }

  end(attributes?: object): void {
    if (this.measureEnd) {
      return;
    }
    this.measureEnd = performance.now();
    this.ended = new Date();
    const elapsedMs = performance.now() - this.measureStart;

    datadogRum.addAction(`${this.component}:${this.spanName}`, {
      ...this.defaultParameters(),
      ended: this.ended,
      elapsedMs,
      ...(attributes || {})
    });
  }
}

const startSpan = (component: string, name: string): DatadogSpan =>
  new DataDogSpanImpl(component, name);

const addVersion = (moduleName: string, version: string) => {
  moduleVersions[moduleName] = version;
};

const addToRumGlobalContext = (key: string, value: object | string) => {
  datadogRum.setGlobalContextProperty(key, value);
}
  
const getRumGlobalContext = (key: string) =>
  datadogRum.getGlobalContext()[key];

const datadogInterface: DatadogInterface = {
  setAuthenticatedUser: userAuthenticated,
  clearAuthenticatedUser,
  startSpan,
  log,
  logError,
  recordException,
  addAction,
  addVersion,
  addToRumGlobalContext,
  getRumGlobalContext
};

export {
  clearAuthenticatedUser,
  DatadogSpan,
  DatadogInterface,
  datadogInterface,
  initializeDataDog,
  log,
  logError,
  recordException,
  startSpan,
  startTrackingEvent,
  userAuthenticated
};
